/**
 * The single place the profile page's replaceable artwork is referenced
 * (APP_SPEC §13.2–§13.3, docs/UI_GUIDELINES.md §11 "one icon mapping"): stat
 * icon sets, the cosmetic catalogs, and the theme presets. Components import
 * from here rather than writing `/assets/...` paths inline, so swapping final
 * art in is an edit to this file (or dropping a same-named file into
 * `apps/web/public/assets/profile/`) and nothing else.
 *
 * `profile-assets.test.ts` asserts every path referenced here exists on disk.
 */
import type { PlatformStatistics } from '@perplexicon/api-client';
import {
  AVATAR_FRAME_IDS,
  HERO_BACKGROUND_IDS,
  PROFILE_TITLE_IDS,
  SIDE_DECORATION_IDS,
} from '@perplexicon/validation';
import type {
  AvatarFrameId,
  HeroBackgroundId,
  ProfileTitleId,
  SideDecorationId,
} from '@perplexicon/validation';

import { assetUrl } from '../../assets/asset-manifest.js';
import { SIDE_DECORATION_METRICS } from './side-decoration-metrics.generated.js';

/* -------------------------------------------------------------------------- */
/* Overall stat icons (the profile hero's "Overall" tiles)                    */
/* -------------------------------------------------------------------------- */

const STAT_ICON_DIR = '/assets/profile/stat-icons';

export const OVERALL_STAT_ICONS: Record<keyof PlatformStatistics, string> = {
  gamesPlayed: `${STAT_ICON_DIR}/played.png`,
  gamesWon: `${STAT_ICON_DIR}/won.png`,
  gamesLost: `${STAT_ICON_DIR}/lost.png`,
  gamesDrawn: `${STAT_ICON_DIR}/drawn.png`,
  gamesAbandoned: `${STAT_ICON_DIR}/abandoned.png`,
};

/* -------------------------------------------------------------------------- */
/* Per-game stat icons (the Gameplay cards' stat grid + streak row)           */
/* -------------------------------------------------------------------------- */

const GAME_STAT_ICON_DIR = '/assets/profile/game-stat-icons';

/** Synthetic stat keys the platform (not a game) owns — the streak row. */
export const STREAK_STAT_KEYS = {
  current: 'currentStreak',
  best: 'bestStreak',
} as const;

/**
 * Maps a stat key (a game-declared `statisticDescriptor.key`, or one of the
 * synthetic streak keys) to its icon file. Unknown keys fall back to a
 * generic stat glyph so a new game's metric always has *an* icon.
 */
const GAME_STAT_ICON_BY_KEY: Record<string, string> = {
  wordsSolved: `${GAME_STAT_ICON_DIR}/words-solved.png`,
  // `rounds-won.png` used to ship as a byte-for-byte copy of `crown.png`, so
  // a Word Rush card drew the same crown for "Rounds won" and for "Best
  // streak" and the two metrics read as one. The file is now the trophy the
  // Overall row uses for a win; the crown remains the best-streak glyph.
  roundsWon: `${GAME_STAT_ICON_DIR}/rounds-won.png`,
  bestWordScore: `${GAME_STAT_ICON_DIR}/best-score.png`,
  // Distinct from `bestWordScore`: both pointed at the same star, so a card
  // showing "Best word score" and "Best game score" showed one glyph twice.
  bestGameScore: `${GAME_STAT_ICON_DIR}/stat.png`,
  fastestSolveMs: `${GAME_STAT_ICON_DIR}/fastest-solve.png`,
  totalGuesses: `${GAME_STAT_ICON_DIR}/total-guesses.png`,
  bestGuessCount: `${GAME_STAT_ICON_DIR}/fewest-guesses.png`,
  // Reuses the win-trophy glyph: Duels Won is Secret Number's own "win", the
  // same concept `roundsWon` draws for Word Rush.
  duelsWon: `${GAME_STAT_ICON_DIR}/rounds-won.png`,
  // A 1-guess solve reads as an instant strike — the same bolt `fastestSolveMs`
  // uses for Word Rush's speed stat, repurposed here for immediacy rather than
  // elapsed time (Secret Number has no timer to measure).
  firstTrySolves: `${GAME_STAT_ICON_DIR}/fastest-solve.png`,
  [STREAK_STAT_KEYS.current]: `${GAME_STAT_ICON_DIR}/current-streak.png`,
  // Best streak reads as a crown (an achievement), not the calendar glyph
  // that previously sat here.
  [STREAK_STAT_KEYS.best]: `${GAME_STAT_ICON_DIR}/crown.png`,
};

/**
 * The glyph beside the "Overall" heading in the hero. A chart, not a generic
 * flourish: the heading introduces counted totals, and the profile's other
 * section heading ("Gameplay") is likewise introduced by its own subject
 * glyph rather than by decoration.
 */
export const OVERALL_HEADING_ICON = `${GAME_STAT_ICON_DIR}/stat.png`;

const GENERIC_GAME_STAT_ICON = `${GAME_STAT_ICON_DIR}/stat.png`;

export function gameStatIcon(key: string): string {
  return GAME_STAT_ICON_BY_KEY[key] ?? GENERIC_GAME_STAT_ICON;
}

/* -------------------------------------------------------------------------- */
/* Cosmetic catalogs                                                          */
/* -------------------------------------------------------------------------- */

export type CosmeticKind = 'avatarFrame' | 'heroBackground' | 'sideDecoration';

export interface CosmeticOption<Id extends string = string> {
  id: Id;
  name: string;
  /** Preview/render asset. Today a neutral placeholder; final art replaces the file in place. */
  asset: string;
  /**
   * Whether this user may equip it. No unlock system exists yet
   * (`docs/APP_PLAN.md`), so these are static for now — a few ship `false`
   * purely so the "only unlocked is selectable" filter in Settings is real.
   * The server independently enforces this (`modules/users/profile-cosmetics.ts`).
   */
  unlocked: boolean;
  sortOrder: number;
}

export interface TitleOption {
  id: ProfileTitleId;
  name: string;
  unlocked: boolean;
  sortOrder: number;
}

const FRAME_NAMES: Record<AvatarFrameId, string> = {
  'ancient-gold': 'Ancient Gold',
  'forgotten-library': 'Forgotten Library',
  'emerald-thorn': 'Emerald Thorn',
  'candlelit-brass': 'Candlelit Brass',
  'runed-oak': 'Runed Oak',
};

const BACKGROUND_NAMES: Record<HeroBackgroundId, string> = {
  'moonlit-library': 'Moonlit Library',
  'forest-ruin': 'Forest Ruin',
  'arcane-study': 'Arcane Study',
  'dawn-keep': 'Dawn Keep',
  'deep-archive': 'Deep Archive',
};

const SIDE_DECORATION_NAMES: Record<SideDecorationId, string> = {
  'dungeon-books': 'Dungeon Books',
  'hanging-banner': 'Hanging Banner',
  'candles-and-tomes': 'Candles & Tomes',
  'ivy-pillar': 'Ivy Pillar',
};

const TITLE_NAMES: Record<ProfileTitleId, string> = {
  'first-light': 'First Light',
  'dungeon-delver': 'Dungeon Delver',
  'word-smith': 'Word Smith',
  'riddle-breaker': 'Riddle Breaker',
  'puzzle-warden': 'Puzzle Warden',
  lorekeeper: 'Lorekeeper',
  archivist: 'Archivist',
  nightscholar: 'Night Scholar',
};

/** Ids that are *not* available to every user yet (proves the unlocked filter). */
const LOCKED_IDS = new Set<string>(['nightscholar', 'runed-oak', 'deep-archive', 'ivy-pillar']);

export const AVATAR_FRAMES: CosmeticOption<AvatarFrameId>[] = AVATAR_FRAME_IDS.map((id, i) => ({
  id,
  name: FRAME_NAMES[id],
  asset: `/assets/profile/cosmetics/frames/${id}.png`,
  unlocked: !LOCKED_IDS.has(id),
  sortOrder: i,
}));

export const HERO_BACKGROUNDS: CosmeticOption<HeroBackgroundId>[] = HERO_BACKGROUND_IDS.map(
  (id, i) => ({
    id,
    name: BACKGROUND_NAMES[id],
    asset: `/assets/profile/cosmetics/backgrounds/${id}.png`,
    unlocked: !LOCKED_IDS.has(id),
    sortOrder: i,
  }),
);

/**
 * A decoration with an entry in `SIDE_DECORATION_METRICS` has a real master
 * processed by `pnpm assets:decorations` (a trimmed `.png` plus measured
 * frame geometry); everything else still resolves to its structural `<id>.svg`
 * placeholder. Adding real art is therefore: drop the master into
 * `assets-src/profile/side-decorations/` and run that command — no edit here.
 */
export const SIDE_DECORATIONS: CosmeticOption<SideDecorationId>[] = SIDE_DECORATION_IDS.map(
  (id, i) => ({
    id,
    name: SIDE_DECORATION_NAMES[id],
    asset: SIDE_DECORATION_METRICS[id]
      ? `/assets/profile/cosmetics/side-decorations/${id}.png`
      : `/assets/profile/cosmetics/side-decorations/${id}.svg`,
    unlocked: !LOCKED_IDS.has(id),
    sortOrder: i,
  }),
);

export const PROFILE_TITLES: TitleOption[] = PROFILE_TITLE_IDS.map((id, i) => ({
  id,
  name: TITLE_NAMES[id],
  unlocked: !LOCKED_IDS.has(id),
  sortOrder: i,
}));

/** Fallback artwork for an empty / unknown cosmetic slot — the structural manifest placeholder. */
const COSMETIC_FALLBACK: Record<CosmeticKind, string> = {
  avatarFrame: assetUrl('profileAvatarFrame'),
  heroBackground: assetUrl('profileHeroBackground'),
  sideDecoration: assetUrl('profileSideDecoration'),
};

const COSMETIC_CATALOG: Record<CosmeticKind, CosmeticOption[]> = {
  avatarFrame: AVATAR_FRAMES,
  heroBackground: HERO_BACKGROUNDS,
  sideDecoration: SIDE_DECORATIONS,
};

/** The asset src for a selected cosmetic id, or the neutral placeholder when unset/unknown. */
export function resolveCosmeticAsset(kind: CosmeticKind, id: string | null): string {
  if (!id) return COSMETIC_FALLBACK[kind];
  return COSMETIC_CATALOG[kind].find((c) => c.id === id)?.asset ?? COSMETIC_FALLBACK[kind];
}

/** The display label for a selected title id, or `null` when no title is selected / the id is unknown. */
export function resolveTitleLabel(id: string | null): string | null {
  if (!id) return null;
  return PROFILE_TITLES.find((t) => t.id === id)?.name ?? null;
}

/* -------------------------------------------------------------------------- */
/* Theme presets                                                             */
/* -------------------------------------------------------------------------- */

/**
 * A convenience that writes all three cosmetic slot ids at once (APP_SPEC
 * §13.2). It is NOT the source of truth — applying a preset just sets three
 * independent fields the user can then change one at a time. The title is
 * deliberately left untouched by presets.
 */
export interface ProfileThemePreset {
  id: string;
  name: string;
  avatarFrameId: AvatarFrameId;
  heroBackgroundId: HeroBackgroundId;
  sideDecorationId: SideDecorationId;
}

export const PROFILE_THEME_PRESETS: ProfileThemePreset[] = [
  {
    id: 'forgotten-library',
    name: 'Forgotten Library',
    avatarFrameId: 'forgotten-library',
    heroBackgroundId: 'moonlit-library',
    sideDecorationId: 'dungeon-books',
  },
  {
    id: 'emerald-ruin',
    name: 'Emerald Ruin',
    avatarFrameId: 'emerald-thorn',
    heroBackgroundId: 'forest-ruin',
    sideDecorationId: 'hanging-banner',
  },
  {
    id: 'candlelit-study',
    name: 'Candlelit Study',
    avatarFrameId: 'candlelit-brass',
    heroBackgroundId: 'arcane-study',
    sideDecorationId: 'candles-and-tomes',
  },
];
